import React, { useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography, Alert, Grid2 } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BITSTRING_LENGTH, TIME_INTERVAL } from '../../constants';

const TOTAL_MINUTES_IN_DAY = 24 * 60;

type BitstringSegment = {
  status: string;
  startBit: number;
  endBit: number;
  startTime: string;
  endTime: string;
  durationBits: number;
  durationPercent: number;
  durationMinutes: number;
};

type BitstringInsights = {
  segments: BitstringSegment[];
  openSegments: BitstringSegment[];
  closedSegments: BitstringSegment[];
  length: number;
  minutesPerBit: number;
  lengthNote?: string;
  hasInvalidSegments: boolean;
};

const formatMinutesTo12Hour = (minutes: number): string => {
  const totalMinutes = ((minutes % TOTAL_MINUTES_IN_DAY) + TOTAL_MINUTES_IN_DAY) % TOTAL_MINUTES_IN_DAY;
  const hours24 = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(mins).padStart(2, '0')} ${period}`;
};

const indexTo12HourTime = (index: number): string => {
  return formatMinutesTo12Hour(index * TIME_INTERVAL);
};

const parseBitstringSegments = (bitstring: string): BitstringSegment[] => {
  const trimmed = bitstring.trim();
  if (!trimmed) return [];

  const segments: BitstringSegment[] = [];
  const len = trimmed.length;
  let cursor = 0;

  while (cursor < len) {
    const status = trimmed[cursor];
    const start = cursor;
    let end = cursor;
    while (end < len && trimmed[end] === status) {
      end++;
    }
    const durationBits = end - start;

    segments.push({
      status,
      startBit: start,
      endBit: end,
      startTime: indexTo12HourTime(start),
      endTime: indexTo12HourTime(end),
      durationBits,
      durationPercent: len === 0 ? 0 : (durationBits / len) * 100,
      durationMinutes: durationBits * TIME_INTERVAL,
    });
    cursor = end;
  }

  return segments;
};

const buildBitstringInsights = (bitstring: string): BitstringInsights => {
  const trimmed = bitstring.trim();
  const segments = parseBitstringSegments(trimmed);
  const length = trimmed.length;
  const minutesPerBit = length === 0 ? 0 : TOTAL_MINUTES_IN_DAY / length;
  const openSegments = segments.filter((segment) => segment.status === '0');
  const closedSegments = segments.filter((segment) => segment.status === '1');
  const hasInvalidSegments = segments.some((segment) => segment.status !== '0' && segment.status !== '1');
  const lengthNote =
    length !== BITSTRING_LENGTH && length > 0
      ? `Note: The input length is ${length} bits, not the expected ${BITSTRING_LENGTH}. Each bit represents approximately ${minutesPerBit.toFixed(2)} minutes.`
      : undefined;

  return {
    segments,
    openSegments,
    closedSegments,
    length,
    minutesPerBit,
    lengthNote,
    hasInvalidSegments,
  };
};

const getSegmentLabel = (status: string) => {
  if (status === '0') return 'Open';
  if (status === '1') return 'Closed';
  return 'Invalid';
};

const renderSegmentList = (segments: BitstringSegment[], emptyLabel: string) => {
  if (!segments.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {segments.map((segment, index) => (
        <Box
          key={`${segment.startBit}-${segment.endBit}-${segment.status}-${index}`}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {segment.startTime} - {segment.endTime}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {segment.durationMinutes} min
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

interface BitstringDebugDialogProps {
  open: boolean;
  onClose: () => void;
  dateLabel?: string;
  dateKey?: string;
  bitstring?: string | null;
}

const BitstringDebugDialog: React.FC<BitstringDebugDialogProps> = ({
  open,
  onClose,
  dateLabel,
  dateKey,
  bitstring,
}) => {
  const theme = useTheme();
  const insights = useMemo(() => {
    if (!bitstring) return null;
    return buildBitstringInsights(bitstring);
  }, [bitstring]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Day Bitstring
        <IconButton color="secondary" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {dateLabel} {dateKey ? `(${dateKey})` : ''}
          </Typography>
          {typeof bitstring === 'string' ? (
            <>
              {insights && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Length: {insights.length} bits (
                    {insights.length === BITSTRING_LENGTH
                      ? `${TIME_INTERVAL} minutes`
                      : `${insights.minutesPerBit.toFixed(2)} minutes`}{' '}
                    per bit)
                  </Typography>
                  {insights.lengthNote && (
                    <Alert severity="info">
                      {insights.lengthNote}
                    </Alert>
                  )}
                  {insights.hasInvalidSegments && (
                    <Alert severity="warning">
                      Bitstring contains characters other than 0 and 1. These segments are marked as "Invalid" below.
                    </Alert>
                  )}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Timeline
                    </Typography>
                    <Box sx={{ position: 'relative', mb: 2, minHeight: 56 }}>
                      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          width: '100%',
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          minHeight: 40,
                          bgcolor: 'background.default',
                        }}
                      >
                        {insights.segments.length ? (
                          insights.segments.map((segment, idx) => {
                            const label = getSegmentLabel(segment.status);
                            const color =
                              segment.status === '0'
                                ? theme.palette.success.main
                                : segment.status === '1'
                                  ? theme.palette.error.main
                                  : theme.palette.warning.main;
                            const contrast = theme.palette.getContrastText(color);
                            return (
                              <Box
                                key={`segment-${segment.startBit}-${segment.endBit}-${idx}`}
                                title={`${label}: ${segment.startTime} - ${segment.endTime} (${segment.durationMinutes} min)`}
                                sx={{
                                  flexGrow: segment.durationBits,
                                  flexBasis: segment.durationBits,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: color,
                                  color: contrast,
                                  px: 0.5,
                                  minWidth: 4,
                                }}
                              >
                                {segment.durationPercent > 7 && (
                                  <Typography variant="caption" fontWeight={700}>
                                    {label}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })
                        ) : (
                          <Box
                            sx={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.secondary',
                            }}
                          >
                            No data to visualize.
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Grid2 container spacing={2}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Open Times (0)
                        </Typography>
                        {renderSegmentList(insights.openSegments, 'No open periods detected.')}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Closed Times (1)
                        </Typography>
                        {renderSegmentList(insights.closedSegments, 'No closed periods detected.')}
                      </Grid2>
                    </Grid2>
                  </Box>
                </>
              )}
            </>
          ) : (
            <Alert severity="warning">
              No bitstring data is cached for this date.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BitstringDebugDialog;
